import { useEffect, useState, useSyncExternalStore } from 'react';

import type { ObjectKey } from '../navigation';
import type { Fetch, RecordHistory } from './historyApi';
import { historyFor, HistoryLoader } from './historyLoader';

/**
 * A record's history, newest first, for the record page: a HistoryLoader
 * (historyLoader.ts, where the paging and its races live and are tested),
 * told whenever the record changes. `record` is null until the record has
 * loaded, and a new object each time it reloads, so a save or a Path step that
 * adds an entry shows it. `fetcher` reaches the API: the record page passes
 * authenticatedFetch.
 */
export function useRecordHistory(
  object: ObjectKey,
  recordId: string,
  record: object | null,
  fetcher: Fetch,
): { history: RecordHistory; showMore: () => void } {
  const [loader] = useState(() => new HistoryLoader());
  const snapshot = useSyncExternalStore(loader.subscribe, loader.getSnapshot);
  useEffect(() => {
    loader.load(fetcher, object, recordId, record !== null);
    // The record reloaded, changed or went away: abandon what is still loading for this one.
    return loader.abandon;
  }, [loader, fetcher, object, recordId, record]);
  return { history: historyFor(snapshot, object, recordId), showMore: loader.showMore };
}
